import React, { useState, useEffect } from 'react';
import { useAuth } from '../lib/auth';
import { api } from '../lib/api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Trash } from 'lucide-react';

const Scrap = () => {
  const { hasRole } = useAuth();
  const [scrapEntries, setScrapEntries] = useState([]);
  const [coilIns, setCoilIns] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    coil_in_id: '',
    job_id: '',
    weight_kg: '',
    scrap_type: '',
    notes: '',
    disposition: ''
  });

  const canCreate = hasRole(['ADMIN', 'OWNER', 'MUNIM', 'OPERATOR']);
  const canSetDisposition = hasRole(['ADMIN', 'OWNER', 'MUNIM']);

  useEffect(() => {
    fetchScrapEntries();
    fetchCoilIns();
    fetchJobs();
  }, []);

  const fetchScrapEntries = async () => {
    try {
      const response = await api.scrapEntries.list();
      setScrapEntries(response.data);
    } catch (error) {
      toast.error('Failed to fetch scrap entries');
    }
  };

  const fetchCoilIns = async () => {
    try {
      const response = await api.coilIns.list({ is_active: true });
      setCoilIns(response.data);
    } catch (error) {
      toast.error('Failed to fetch coil entries');
    }
  };

  const fetchJobs = async () => {
    try {
      const response = await api.jobs.list();
      setJobs(response.data);
    } catch (error) {
      toast.error('Failed to fetch jobs');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.scrapEntries.create({
        coil_in_id: formData.coil_in_id,
        job_id: formData.job_id || null,
        weight_kg: parseFloat(formData.weight_kg),
        scrap_type: formData.scrap_type,
        notes: formData.notes || null,
        photos: [],
        disposition: formData.disposition || null
      });
      toast.success('Scrap entry created successfully');
      setDialogOpen(false);
      resetForm();
      fetchScrapEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create scrap entry');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      coil_in_id: '',
      job_id: '',
      weight_kg: '',
      scrap_type: '',
      notes: '',
      disposition: ''
    });
  };

  const getDispositionBadge = (disposition) => {
    if (!disposition) {
      return (
        <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-grey bg-industrial-s2 border border-industrial-border px-2 py-0.5">
          <span className="led-indicator bg-led-grey" /> NOT SET
        </div>
      );
    }
    switch (disposition) {
      case 'YARD':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-amber bg-industrial-s2 border border-industrial-border px-2 py-0.5">
            <span className="led-indicator bg-led-amber" /> YARD
          </div>
        );
      case 'SOLD_ON_CUSTOMER_BEHALF':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-green bg-industrial-s2 border border-industrial-border px-2 py-0.5">
            <span className="led-indicator bg-led-green" /> SOLD FOR CUSTOMER
          </div>
        );
      case 'DISPATCHED_BACK':
        return (
          <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-sky-400 bg-industrial-s2 border border-industrial-border px-2 py-0.5">
            <span className="led-indicator bg-sky-500" /> DISPATCHED BACK
          </div>
        );
      default:
        return (
          <div className="flex items-center gap-1.5 font-mono text-[9px] uppercase font-bold text-led-grey bg-industrial-s2 border border-industrial-border px-2 py-0.5">
            <span className="led-indicator bg-led-grey" /> {disposition}
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-industrial-bg text-white p-6 space-y-6" data-testid="scrap-page">
      {/* Warning Panel */}
      <div className="border-l-4 border-led-amber bg-industrial-s1 p-4 border border-industrial-border">
        <p className="text-sm font-semibold text-led-amber font-mono uppercase tracking-wider">Warning: Legacy / Non-Canonical Module</p>
        <p className="text-xs text-slate-400 mt-1 font-mono uppercase">
          Use the <strong className="text-white underline">Scrap Dispatch</strong> page for canonical settlement and financial ledger reconciliation.
        </p>
      </div>

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-industrial-border pb-4">
        <div>
          <h1 className="font-heading text-3xl font-bold tracking-tight uppercase">Scrap Management</h1>
          <p className="text-slate-400 mt-2 font-mono text-xs uppercase font-medium">Customer-owned scrap tracking</p>
        </div>
        
        {canCreate && (
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <button 
                onClick={resetForm} 
                className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-4 flex items-center justify-center gap-1.5 transition-colors"
                data-testid="create-scrap-button"
              >
                <Plus className="h-4 w-4" /> RECORD SCRAP
              </button>
            </DialogTrigger>
            
            <DialogContent className="bg-industrial-s1 border border-industrial-border text-white rounded-none max-w-2xl p-6">
              <DialogHeader>
                <DialogTitle className="font-heading text-lg font-bold uppercase tracking-wide border-b border-industrial-border pb-2 text-white">Record Scrap Entry</DialogTitle>
              </DialogHeader>
              
              <form onSubmit={handleSubmit} className="space-y-4 text-xs mt-2">
                <div>
                  <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Coil *</label>
                  <select 
                    value={formData.coil_in_id} 
                    onChange={(e) => setFormData({ ...formData, coil_in_id: e.target.value })}
                    className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                    data-testid="scrap-coil-select"
                    required
                  >
                    <option value="">Select coil</option>
                    {coilIns.map(coil => (
                      <option key={coil.id} value={coil.id}>
                        {coil.party_name} - {new Date(coil.inward_datetime).toLocaleDateString()}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Job (Optional)</label>
                  <select 
                    value={formData.job_id} 
                    onChange={(e) => setFormData({ ...formData, job_id: e.target.value })}
                    className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                  >
                    <option value="">None</option>
                    {jobs.map(job => (
                      <option key={job.id} value={job.id}>
                        Job #{job.id.slice(0, 8)} - {job.operator_name}
                      </option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label htmlFor="weight_kg" className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Weight (kg) *</label>
                  <input
                    id="weight_kg"
                    type="number"
                    step="0.1"
                    className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                    value={formData.weight_kg}
                    onChange={(e) => setFormData({ ...formData, weight_kg: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="scrap_type" className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Scrap Type *</label>
                  <input
                    id="scrap_type"
                    placeholder="e.g., Edge Trim, Defective Circles"
                    className="w-full h-10 px-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                    value={formData.scrap_type}
                    onChange={(e) => setFormData({ ...formData, scrap_type: e.target.value })}
                    required
                  />
                </div>
                
                <div>
                  <label htmlFor="notes" className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Notes</label>
                  <textarea
                    id="notes"
                    className="w-full min-h-[80px] p-3 bg-industrial-s2 border border-industrial-border font-mono text-white rounded-none focus:border-accent focus:outline-none"
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                  />
                </div>
                
                {canSetDisposition && (
                  <div>
                    <label className="block uppercase text-[10px] tracking-wider text-slate-400 font-sans mb-1 font-semibold font-bold">Disposition</label>
                    <select 
                      value={formData.disposition} 
                      onChange={(e) => setFormData({ ...formData, disposition: e.target.value })}
                      className="flex h-10 w-full items-center rounded-none border border-industrial-border bg-industrial-s2 px-3 py-2 text-xs font-mono text-white focus:outline-none focus:border-accent"
                    >
                      <option value="">Not Set</option>
                      <option value="YARD">Yard</option>
                      <option value="SOLD_ON_CUSTOMER_BEHALF">Sold on Customer Behalf</option>
                      <option value="DISPATCHED_BACK">Dispatched Back</option>
                    </select>
                  </div>
                )}
                
                <div className="flex gap-2 justify-end border-t border-industrial-border pt-4 mt-2">
                  <button 
                    type="button" 
                    onClick={() => setDialogOpen(false)}
                    className="bg-industrial-s2 border border-industrial-border hover:bg-neutral-800 text-white rounded-none font-mono text-xs uppercase tracking-wider h-11 px-4 transition-colors"
                  >
                    CANCEL
                  </button>
                  <button 
                    type="submit" 
                    disabled={loading} 
                    className="bg-accent text-white font-mono uppercase tracking-wider font-bold rounded-none border-b-2 border-black/30 hover:bg-accent-hover h-11 px-6 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    data-testid="submit-scrap"
                  >
                    {loading ? 'CREATING...' : 'CREATE ENTRY'}
                  </button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      <div className="bg-industrial-s1 border border-industrial-border p-6 space-y-4">
        <div className="flex items-center gap-2 border-b border-industrial-border pb-3 mb-4">
          <Trash className="h-5 w-5 text-slate-400" />
          <h2 className="text-xs uppercase font-sans tracking-wider text-slate-400 font-bold">Scrap Entries</h2>
        </div>
        
        {scrapEntries.length === 0 ? (
          <div className="text-center py-8 text-slate-500 font-mono text-xs uppercase">No scrap entries yet</div>
        ) : (
          <div className="space-y-3">
            {scrapEntries.map(entry => (
              <div key={entry.id} className="border border-industrial-border bg-industrial-s2/20 p-4 font-mono text-xs hover:bg-industrial-s2/30 transition-colors">
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-2 border-b border-industrial-border/30 pb-2 mb-2">
                  <div className="flex-1">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <span className="font-bold text-white text-sm font-mono">{entry.weight_kg} kg</span>
                      {getDispositionBadge(entry.disposition)}
                    </div>
                    <p className="text-xs font-sans text-slate-300"><span className="font-mono text-slate-500 uppercase">TYPE:</span> {entry.scrap_type}</p>
                    {entry.notes && (
                      <p className="text-xs text-slate-400 bg-industrial-s2 p-2 border border-industrial-border/60 mt-2 break-words">{entry.notes}</p>
                    )}
                  </div>
                </div>
                <p className="text-[10px] text-slate-500 mt-1 uppercase">
                  RECORDED AT: {new Date(entry.created_at).toLocaleString()}
                </p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Scrap;
